import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Slider from "react-slick";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useLoginModal } from "../../Context/LoginModalContext";
import { checkLoggedIn } from "../../utils";

const seedReviews = [
  {
    name: "Ahmed Al Mansoori",
    location: "Dubai, UAE",
    rating: 5,
    createdAt: null,
    dateLabel: "2 days ago",
    title: "Instant delivery, zero hassle",
    body:
      "Ordered an Amazon gift card at midnight and received it within 30 seconds. The support team even followed up to confirm everything worked. Trusted them ever since.",
    verified: true,
  },
  {
    name: "Sarah Thompson",
    location: "London, UK",
    rating: 5,
    createdAt: null,
    dateLabel: "1 week ago",
    title: "Best gift card store online",
    body:
      "I've used many platforms for digital vouchers, but GlobalGiftPass is by far the most reliable. Clean UI, fast checkout, and genuine codes every time.",
    verified: true,
  },
  {
    name: "Mohammed Khan",
    location: "Riyadh, KSA",
    rating: 5,
    createdAt: null,
    dateLabel: "3 weeks ago",
    title: "Fantastic corporate support",
    body:
      "We bulk-ordered 250 gift cards for our team. Delivery was instant and the team was responsive throughout. Will order again next quarter.",
    verified: true,
  },
  {
    name: "Priya Sharma",
    location: "Mumbai, India",
    rating: 5,
    createdAt: null,
    dateLabel: "1 month ago",
    title: "Easy and secure",
    body:
      "Bought a gift card for a surprise to my family — clean checkout, instant delivery and the WhatsApp channel made it easy to confirm everything. Felt safe using them.",
    verified: true,
  },
];

const Stars = ({ rating = 5, size = 18, interactive = false, onChange }) => (
  <div className="ggp-tr-stars" aria-label={`${rating} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => {
      const filled = i <= rating;
      const Tag = interactive ? "button" : "span";
      return (
        <Tag
          key={i}
          type={interactive ? "button" : undefined}
          className={interactive ? "ggp-tr-star-btn" : undefined}
          onClick={interactive ? () => onChange?.(i) : undefined}
          aria-label={interactive ? `Rate ${i} out of 5` : undefined}
        >
          <svg
            viewBox="0 0 24 24"
            width={size}
            height={size}
            fill={filled ? "#00b67a" : "#2a2d33"}
            aria-hidden="true"
          >
            <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.402 8.172L12 18.896l-7.336 3.864 1.4-8.172L.132 9.211l8.2-1.193z" />
          </svg>
        </Tag>
      );
    })}
  </div>
);

const relativeDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return "Today";
  if (diff < 2 * day) return "Yesterday";
  if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))} weeks ago`;
  if (diff < 365 * day) return `${Math.floor(diff / (30 * day))} months ago`;
  return `${Math.floor(diff / (365 * day))} years ago`;
};

const TrustReviews = () => {
  const [dbReviews, setDbReviews] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: "", body: "" });
  const [detailReview, setDetailReview] = useState(null);

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      { breakpoint: 1100, settings: { slidesToShow: 3, slidesToScroll: 1 } },
      { breakpoint: 900, settings: { slidesToShow: 2, slidesToScroll: 1 } },
      { breakpoint: 600, settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
  };

  const { openLoginModal } = useLoginModal();
  const token = useSelector((state) => state?.configs?.token);

  const fetchReviews = () => {
    axios
      .get("get-reviews", { params: { limit: 12 } })
      .then((res) => {
        setDbReviews(Array.isArray(res?.data?.data) ? res.data.data : []);
      })
      .catch(() => setDbReviews([]));
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const displayedReviews = useMemo(() => {
    const visible = dbReviews
      .filter((r) => (r?.rating || 0) >= 3)
      .map((r) => ({ ...r, dateLabel: relativeDate(r.createdAt) }));
    if (visible.length >= 4) return visible.slice(0, 12);
    return [...visible, ...seedReviews].slice(0, 8);
  }, [dbReviews]);

  const handleWriteReviewClick = () => {
    if (!checkLoggedIn()) {
      openLoginModal();
      return;
    }
    setForm({ rating: 5, title: "", body: "" });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Please fill in both the title and the review.");
      return;
    }
    setSubmitting(true);
    toast.dismiss();
    try {
      const { data } = await axios.post(
        "add-review",
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(data?.message || "Thank you for your review!");
      setModalOpen(false);
      fetchReviews();
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      toast.error(apiMessage || "Could not submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="ggp-tr-section" aria-labelledby="ggp-tr-title">
      <div className="ggp-tr-inner">
        <div className="ggp-tr-head">
          <span className="ggp-tr-eyebrow">Trusted by thousands worldwide</span>
          <h2 id="ggp-tr-title" className="ggp-tr-title">
            Reviews, Response Times & Delivery You Can Count On
          </h2>
          <p className="ggp-tr-sub">
            Real customers. Real feedback. Real numbers behind every order.
          </p>
        </div>

        <div className="ggp-tr-metrics">
          <div className="ggp-tr-metric">
            <div className="ggp-tr-metric-num">
              4.9<span>/5</span>
            </div>
            <Stars rating={5} />
            <div className="ggp-tr-metric-label">
              Based on <strong>12,480+</strong> verified reviews
            </div>
          </div>

          <div className="ggp-tr-metric">
            <div className="ggp-tr-metric-num">
              &lt; 2<span>hrs</span>
            </div>
            <div className="ggp-tr-metric-icon">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="#00b67a" aria-hidden="true">
                <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 11h-4v-2h2V6h2z" />
              </svg>
            </div>
            <div className="ggp-tr-metric-label">
              Average <strong>support response</strong> time
            </div>
          </div>

          <div className="ggp-tr-metric">
            <div className="ggp-tr-metric-num">
              98<span>%</span>
            </div>
            <div className="ggp-tr-metric-icon">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="#00b67a" aria-hidden="true">
                <path d="M13 2L3 14h7l-1 8 10-12h-7z" />
              </svg>
            </div>
            <div className="ggp-tr-metric-label">
              Orders <strong>delivered instantly</strong>
            </div>
          </div>
        </div>

        <div className="ggp-tr-reviewbar">
          <div className="ggp-tr-reviewbar-left">
            <span className="ggp-tr-logo-mark" aria-hidden="true">★</span>
            <strong>Excellent</strong>
            <Stars rating={5} />
          </div>
          <div className="ggp-tr-reviewbar-right">
            <span>
              <strong>4.9</strong> · Verified Customer Reviews
            </span>
            <button
              type="button"
              className="ggp-tr-write-btn"
              onClick={handleWriteReviewClick}
            >
              Write a Review
            </button>
          </div>
        </div>

        <div className="ggp-tr-slider">
          <Slider {...sliderSettings}>
            {displayedReviews.map((r, idx) => (
              <div key={r._id || idx} className="ggp-tr-slide">
                <article className="ggp-tr-card">
                  <div className="ggp-tr-card-top">
                    <Stars rating={r.rating} />
                    <span className="ggp-tr-date">{r.dateLabel}</span>
                  </div>
                  <h3 className="ggp-tr-card-title">{r.title}</h3>
                  <p className="ggp-tr-card-body ggp-tr-clamp">{r.body}</p>
                  {(r.body || "").length > 220 && (
                    <button
                      type="button"
                      className="ggp-tr-readmore"
                      onClick={() => setDetailReview(r)}
                    >
                      Read more
                    </button>
                  )}
                  <div className="ggp-tr-card-foot">
                    <div className="ggp-tr-avatar" aria-hidden="true">
                      {(r.name || "?")
                        .split(" ")
                        .map((n) => n[0])
                        .slice(0, 2)
                        .join("")}
                    </div>
                    <div className="ggp-tr-meta">
                      <div className="ggp-tr-name">
                        {r.name}
                        {r.verified && (
                          <span className="ggp-tr-verified" title="Verified Customer">
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="#00b67a" aria-hidden="true">
                              <path d="M12 2L3 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6z" />
                              <path d="M10 14.17L7.83 12l-1.42 1.41L10 17l8-8-1.41-1.41z" fill="#fff" />
                            </svg>
                            Verified
                          </span>
                        )}
                      </div>
                      {r.location && <div className="ggp-tr-loc">{r.location}</div>}
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </Slider>
        </div>
      </div>

      {detailReview && (
        <div className="ggp-tr-modal-backdrop" onClick={() => setDetailReview(null)}>
          <div className="ggp-tr-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="ggp-tr-modal-close"
              onClick={() => setDetailReview(null)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="ggp-tr-detail-top">
              <Stars rating={detailReview.rating} />
              <span className="ggp-tr-date">{detailReview.dateLabel}</span>
            </div>
            <h3 className="ggp-tr-detail-title">{detailReview.title}</h3>
            <p className="ggp-tr-detail-body">{detailReview.body}</p>
            <div className="ggp-tr-card-foot ggp-tr-detail-foot">
              <div className="ggp-tr-avatar" aria-hidden="true">
                {(detailReview.name || "?")
                  .split(" ")
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join("")}
              </div>
              <div className="ggp-tr-meta">
                <div className="ggp-tr-name">
                  {detailReview.name}
                  {detailReview.verified && (
                    <span className="ggp-tr-verified" title="Verified Customer">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="#00b67a" aria-hidden="true">
                        <path d="M12 2L3 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6z" />
                        <path d="M10 14.17L7.83 12l-1.42 1.41L10 17l8-8-1.41-1.41z" fill="#fff" />
                      </svg>
                      Verified
                    </span>
                  )}
                </div>
                {detailReview.location && <div className="ggp-tr-loc">{detailReview.location}</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="ggp-tr-modal-backdrop" onClick={() => !submitting && setModalOpen(false)}>
          <div className="ggp-tr-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="ggp-tr-modal-close"
              onClick={() => !submitting && setModalOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3>Share your experience</h3>
            <p className="ggp-tr-modal-sub">
              Help other shoppers by telling them what you thought.
            </p>
            <form onSubmit={handleSubmit}>
              <div className="ggp-tr-field">
                <label>Your rating</label>
                <Stars
                  rating={form.rating}
                  size={28}
                  interactive
                  onChange={(v) => setForm((f) => ({ ...f, rating: v }))}
                />
              </div>
              <div className="ggp-tr-field">
                <label>Title</label>
                <input
                  type="text"
                  maxLength={120}
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Summarise your experience"
                />
              </div>
              <div className="ggp-tr-field">
                <label>Your review</label>
                <textarea
                  rows={4}
                  maxLength={1000}
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder="Tell us about your purchase, delivery speed, support..."
                />
              </div>
              <button type="submit" className="ggp-tr-submit" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit review"}
              </button>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .ggp-tr-section {
          background: #000000;
          background-image:
            radial-gradient(circle at 15% 10%, rgba(0, 182, 122, 0.08), transparent 40%),
            radial-gradient(circle at 85% 90%, rgba(0, 182, 122, 0.06), transparent 45%);
          padding: 80px 20px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #e6e8ec;
        }
        .ggp-tr-inner { max-width: 1200px; margin: 0 auto; width: 100%; }
        .ggp-tr-head { text-align: center; margin-bottom: 48px; }
        .ggp-tr-eyebrow {
          display: inline-block;
          background: rgba(0, 182, 122, 0.14);
          color: #00d488;
          font-weight: 600;
          font-size: 12px;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          padding: 6px 14px;
          border-radius: 999px;
          margin-bottom: 14px;
          border: 1px solid rgba(0, 182, 122, 0.25);
        }
        .ggp-tr-title {
          font-size: 34px;
          font-weight: 700;
          margin: 0 0 12px;
          line-height: 1.25;
          color: #ffffff;
        }
        .ggp-tr-sub { font-size: 16px; color: #9ca3af; margin: 0; }

        .ggp-tr-metrics {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 40px;
        }
        .ggp-tr-metric {
          background: #111111;
          border: 1px solid #1f2126;
          border-radius: 16px;
          padding: 28px 24px;
          text-align: center;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
          transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
        }
        .ggp-tr-metric:hover {
          transform: translateY(-4px);
          border-color: rgba(0, 182, 122, 0.35);
          box-shadow: 0 10px 28px rgba(0, 182, 122, 0.12);
        }
        .ggp-tr-metric-num {
          font-size: 44px;
          font-weight: 800;
          color: #ffffff;
          line-height: 1;
          margin-bottom: 10px;
        }
        .ggp-tr-metric-num span {
          font-size: 18px;
          color: #9ca3af;
          margin-left: 2px;
          font-weight: 600;
        }
        .ggp-tr-metric-icon { margin: 4px 0 8px; display: flex; justify-content: center; }
        .ggp-tr-metric-label { font-size: 14px; color: #b0b6c0; }
        .ggp-tr-metric-label strong { color: #ffffff; }
        .ggp-tr-stars { display: inline-flex; gap: 2px; align-items: center; justify-content: center; }
        .ggp-tr-star-btn {
          background: transparent;
          border: none;
          padding: 2px;
          cursor: pointer;
          line-height: 0;
        }
        .ggp-tr-star-btn:focus { outline: 2px solid rgba(0,182,122,0.5); border-radius: 4px; }

        .ggp-tr-reviewbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: linear-gradient(90deg, #0a0a0a 0%, #151515 100%);
          border: 1px solid #1f2126;
          color: #fff;
          padding: 14px 22px;
          border-radius: 12px;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .ggp-tr-reviewbar-left { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .ggp-tr-reviewbar-left strong { font-size: 16px; color: #fff; }
        .ggp-tr-logo-mark {
          background: #00b67a;
          color: #fff;
          width: 28px;
          height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          font-weight: 700;
        }
        .ggp-tr-reviewbar-right {
          font-size: 14px;
          color: #b0b6c0;
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .ggp-tr-reviewbar-right strong { color: #fff; }
        .ggp-tr-write-btn {
          background: #00b67a;
          color: #fff;
          border: none;
          padding: 8px 18px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background .2s ease, transform .2s ease;
        }
        .ggp-tr-write-btn:hover { background: #00d488; transform: translateY(-1px); }

        .ggp-tr-slider {
          padding-bottom: 40px;
          margin: 0 -10px;
        }
        .ggp-tr-slider .slick-track { display: flex; align-items: stretch; }
        .ggp-tr-slider .slick-slide { height: auto; }
        .ggp-tr-slider .slick-slide > div { height: 100%; padding: 0 10px; display: flex; }
        .ggp-tr-slide { height: 100%; width: 100%; display: flex; }
        .ggp-tr-slider .slick-dots li button:before { color: #00b67a; opacity: 0.35; font-size: 8px; }
        .ggp-tr-slider .slick-dots li.slick-active button:before { color: #00d488; opacity: 1; }
        .ggp-tr-slider .slick-prev, .ggp-tr-slider .slick-next { z-index: 2; width: 36px; height: 36px; }
        .ggp-tr-slider .slick-prev { left: -8px; }
        .ggp-tr-slider .slick-next { right: -8px; }
        .ggp-tr-slider .slick-prev:before, .ggp-tr-slider .slick-next:before {
          color: #00b67a; font-size: 36px; opacity: 0.85;
        }
        .ggp-tr-card {
          background: #111111;
          border: 1px solid #1f2126;
          border-radius: 14px;
          padding: 22px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
          transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
          width: 100%;
        }
        .ggp-tr-card:hover {
          transform: translateY(-3px);
          border-color: rgba(0, 182, 122, 0.35);
          box-shadow: 0 10px 28px rgba(0, 182, 122, 0.12);
        }
        .ggp-tr-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .ggp-tr-date { font-size: 12px; color: #8b92a0; }
        .ggp-tr-card-title {
          font-size: 16px;
          font-weight: 700;
          margin: 0;
          color: #ffffff;
        }
        .ggp-tr-card-body {
          font-size: 14px;
          color: #b0b6c0;
          line-height: 1.55;
          margin: 0;
          flex-grow: 1;
        }
        .ggp-tr-clamp {
          display: -webkit-box;
          -webkit-line-clamp: 5;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ggp-tr-readmore {
          align-self: flex-start;
          background: transparent;
          border: none;
          color: #00d488;
          font-size: 13px;
          font-weight: 600;
          padding: 0;
          cursor: pointer;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .ggp-tr-readmore:hover { color: #00b67a; }
        .ggp-tr-card-foot {
          display: flex;
          align-items: center;
          gap: 10px;
          border-top: 1px solid #1f2126;
          padding-top: 12px;
        }
        .ggp-tr-avatar {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: linear-gradient(135deg, #00b67a, #0b9163);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
          flex-shrink: 0;
        }
        .ggp-tr-name {
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }
        .ggp-tr-verified {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-size: 11px;
          font-weight: 600;
          color: #00d488;
          background: rgba(0, 182, 122, 0.15);
          padding: 2px 6px;
          border-radius: 4px;
        }
        .ggp-tr-loc { font-size: 12px; color: #8b92a0; }

        .ggp-tr-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .ggp-tr-modal {
          background: #111;
          border: 1px solid #1f2126;
          border-radius: 16px;
          padding: 32px;
          width: 100%;
          max-width: 480px;
          color: #e6e8ec;
          position: relative;
          box-shadow: 0 24px 60px rgba(0,0,0,0.6);
        }
        .ggp-tr-modal h3 { color: #fff; margin: 0 0 6px; font-size: 22px; font-weight: 700; }
        .ggp-tr-modal-sub { color: #9ca3af; font-size: 14px; margin: 0 0 20px; }
        .ggp-tr-modal-close {
          position: absolute;
          top: 12px;
          right: 14px;
          background: transparent;
          border: none;
          color: #9ca3af;
          font-size: 28px;
          line-height: 1;
          cursor: pointer;
        }
        .ggp-tr-modal-close:hover { color: #fff; }
        .ggp-tr-field { margin-bottom: 16px; display: flex; flex-direction: column; }
        .ggp-tr-field label {
          font-size: 12px;
          color: #9ca3af;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
          font-weight: 600;
        }
        .ggp-tr-field input,
        .ggp-tr-field textarea {
          background: #0a0a0a;
          border: 1px solid #2a2d33;
          color: #fff;
          border-radius: 8px;
          padding: 11px 14px;
          font-size: 14px;
          font-family: inherit;
          outline: none;
          transition: border-color .2s ease;
        }
        .ggp-tr-field input:focus,
        .ggp-tr-field textarea:focus { border-color: #00b67a; }
        .ggp-tr-field textarea { resize: vertical; min-height: 100px; }
        .ggp-tr-submit {
          width: 100%;
          background: #00b67a;
          color: #fff;
          border: none;
          padding: 13px 22px;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: background .2s ease, transform .2s ease;
          margin-top: 4px;
        }
        .ggp-tr-submit:hover:not(:disabled) { background: #00d488; transform: translateY(-1px); }
        .ggp-tr-submit:disabled { opacity: 0.7; cursor: not-allowed; }

        .ggp-tr-detail-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .ggp-tr-detail-title {
          color: #fff;
          font-size: 20px;
          font-weight: 700;
          margin: 0 0 14px;
          line-height: 1.3;
        }
        .ggp-tr-detail-body {
          color: #d1d5db;
          font-size: 15px;
          line-height: 1.7;
          margin: 0 0 20px;
          white-space: pre-wrap;
          max-height: 55vh;
          overflow-y: auto;
        }
        .ggp-tr-detail-foot { border-top: 1px solid #1f2126; padding-top: 14px; }

        @media (max-width: 768px) {
          .ggp-tr-section { padding: 56px 16px; }
          .ggp-tr-title { font-size: 24px; }
          .ggp-tr-sub { font-size: 14px; }
          .ggp-tr-head { margin-bottom: 32px; }
          .ggp-tr-metrics { grid-template-columns: 1fr; gap: 14px; margin-bottom: 28px; }
          .ggp-tr-metric { padding: 22px 18px; }
          .ggp-tr-metric-num { font-size: 36px; }
          .ggp-tr-reviewbar { padding: 12px 16px; font-size: 14px; flex-direction: column; align-items: flex-start; }
          .ggp-tr-reviewbar-right { font-size: 13px; }
          .ggp-tr-card { padding: 18px; }
          .ggp-tr-card-title { font-size: 15px; }
          .ggp-tr-card-body { font-size: 13px; }
          .ggp-tr-modal { padding: 24px; }
          .ggp-tr-detail-title { font-size: 18px; }
          .ggp-tr-detail-body { font-size: 14px; }
        }
        @media (max-width: 520px) {
          .ggp-tr-section { padding: 44px 14px; }
          .ggp-tr-title { font-size: 22px; }
          .ggp-tr-metric-num { font-size: 32px; }
        }
      `}</style>
    </section>
  );
};

export default TrustReviews;
